import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
  type AutoscaleInfo,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
} from 'lightweight-charts'
import { useEffect, useRef } from 'react'

import type { Candle } from '@/lib/types'

/** Une bougie au format de la bibliothèque : prix en nombres, pour l'affichage seul. */
function toBar(candle: Candle) {
  return {
    time: candle.time as never,
    open: Number(candle.open),
    high: Number(candle.high),
    low: Number(candle.low),
    close: Number(candle.close),
  }
}

/** Traduit nos options de courbe dans le vocabulaire de la bibliothèque. */
function lineOptions(options: { color: string; width: number; dashed: boolean }) {
  return {
    color: options.color,
    lineWidth: options.width as 1 | 2 | 3 | 4,
    lineStyle: options.dashed ? LineStyle.Dashed : LineStyle.Solid,
  }
}

/** Une ligne horizontale sur le graphique (entrée, stop, cible). */
export interface PriceMarker {
  price: number
  color: string
  title: string
  dashed?: boolean
}

/**
 * Conversions prix <-> pixels, exposées à l'overlay des niveaux déplaçables.
 *
 * L'overlay est du HTML positionné au-dessus du canevas : il a besoin de
 * traduire un prix en ordonnée pour placer ses poignées, et une ordonnée en
 * prix quand le doigt les déplace.
 */
export interface ChartApi {
  priceToY: (price: number) => number | null
  yToPrice: (y: number) => number | null
  /**
   * Conversions temps <-> abscisse, nécessaires aux tracés obliques.
   *
   * Un segment est ancré sur deux instants, pas sur deux positions à l'écran :
   * sans cela il se décalerait au premier défilement et ne désignerait plus les
   * mêmes bougies.
   */
  timeToX: (time: number) => number | null
  xToTime: (x: number) => number | null
  /** Recadre sur l'ensemble des bougies et réactive l'échelle automatique. */
  fit: () => void
  /**
   * Ouvre une courbe superposée, pour les indicateurs.
   *
   * Les indicateurs sont dessinés PAR la bibliothèque, et non en HTML posé
   * par-dessus comme les niveaux déplaçables. Une moyenne mobile suit la
   * courbe des prix sur toute sa longueur : la redessiner à chaque image
   * depuis React coûterait des centaines de points par bougie, alors que le
   * canevas sait la tracer une fois pour toutes et la faire défiler avec le
   * reste.
   *
   * Renvoie `null` si le graphique a déjà été détruit.
   */
  addLine: (options: LineOptions) => LineHandle | null
}

export interface LineOptions {
  color: string
  width: number
  dashed: boolean
}

/**
 * Poignée d'une courbe superposée.
 *
 * Enveloppe volontairement le type de la bibliothèque : le reste de
 * l'application ne manipule jamais `ISeriesApi` directement, et chaque
 * opération reste sans effet une fois la courbe retirée — l'ordre de
 * démontage entre React et le graphique n'est pas garanti.
 */
export interface LineHandle {
  setData: (points: { time: number; value: number }[]) => void
  /** Remplace le dernier point, ou en ajoute un plus récent. */
  update: (point: { time: number; value: number }) => void
  applyOptions: (options: LineOptions) => void
  remove: () => void
}

/**
 * Graphique en chandeliers.
 *
 * La bibliothèque `lightweight-charts` gère elle-même son canevas : React ne
 * doit pas toucher à son contenu. Le composant se contente donc de créer le
 * graphique une fois, puis de lui pousser les données — d'où les `useRef`
 * plutôt qu'un état React, qui redessinerait tout à chaque tick.
 *
 * Les prix arrivent du backend en **chaînes** (précision préservée) ; ils ne
 * sont convertis en nombres qu'ici, pour l'affichage. Aucun calcul de risque
 * ne passe par ces valeurs.
 */
export function PriceChart({
  candles,
  markers = [],
  height = 320,
  digits = 5,
  fitKey = '',
  includePrices = [],
  onReady,
  children,
}: {
  candles: Candle[]
  markers?: PriceMarker[]
  height?: number
  /**
   * Décimales de la paire (5 pour EURUSD, 3 pour USDJPY).
   *
   * Sans cette information, `lightweight-charts` applique son format par
   * défaut — deux décimales. L'échelle affichait donc « 1.12 » quel que soit
   * le zoom, et deux niveaux distants de dix pips portaient la même
   * graduation. C'est l'unité de la paire qui doit décider, jamais une
   * valeur par défaut de la bibliothèque.
   */
  digits?: number
  /**
   * Identité du jeu de données (paire + intervalle).
   *
   * Le recadrage automatique n'a lieu QUE lorsque cette clé change. Recadrer
   * à chaque rafraîchissement des bougies annulait le zoom de l'utilisateur
   * dès la bougie suivante : le graphique était impossible à explorer.
   */
  fitKey?: string
  /**
   * Prix à garder dans la plage visible (stop, cible).
   *
   * Sans cela, un niveau posé loin du marché sort du cadre : sa poignée se
   * colle au bord de l'écran, ou disparaît, et devient impossible à
   * rattraper. L'échelle s'élargit pour les inclure — jusqu'à ce que
   * l'utilisateur étire lui-même l'échelle des prix, ce qui désactive
   * l'ajustement automatique et lui rend la main.
   */
  includePrices?: number[]
  /** Reçoit les conversions dès que le graphique est prêt. */
  onReady?: (api: ChartApi | null) => void
  /** Overlay rendu au-dessus du canevas (niveaux déplaçables). */
  children?: React.ReactNode
}) {
  const container = useRef<HTMLDivElement>(null)
  // Gardé dans une référence : le graphique n'est créé qu'une fois, il ne doit
  // pas être reconstruit quand le parent change de fonction de rappel.
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const chart = useRef<IChartApi | null>(null)
  const series = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const priceLines = useRef<IPriceLine[]>([])
  const fitted = useRef<string | null>(null)
  // Dernière série transmise au graphique, pour reconnaître une simple mise
  // à jour de la dernière bougie.
  const shown = useRef<Candle[]>([])

  // --- Création (une seule fois) -------------------------------------------
  useEffect(() => {
    if (!container.current) return

    // Le graphique survit à la fermeture par ses poignées : ce drapeau leur
    // dit qu'il n'existe plus.
    let disposed = false

    const instance = createChart(container.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#131722' },
        textColor: '#787b86',
        fontFamily:
          "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Monaco, monospace",
        fontSize: 11,
        /*
         * Pas de logo posé sur le graphique.
         *
         * La bibliothèque en pose un en bas à gauche — exactement là où
         * s'affiche l'étiquette du niveau bas. Sur un achat, c'est le STOP :
         * la distance et le montant risqué étaient illisibles, et le logo
         * interceptait même l'appui censé ouvrir la saisie exacte. Le crédit
         * dû à TradingView est donné dans les réglages, où il est lisible.
         */
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1c2030' },
        horzLines: { color: '#1c2030' },
      },
      rightPriceScale: {
        borderColor: '#2a2e39',
        // De la place au-dessus et en dessous : sans marge, une bougie
        // extrême colle au bord et l'étiquette de son niveau déborde.
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor: '#2a2e39',
        timeVisible: true,
        secondsVisible: false,
        // `minBarSpacing` bas : indispensable pour dézoomer sur dix-huit mois
        // de M15 sans que la bibliothèque refuse de resserrer les bougies.
        minBarSpacing: 0.05,
        rightOffset: 6,
        // Le zoom survit au changement de taille de la fenêtre (rotation du
        // téléphone, passage en plein écran).
        lockVisibleTimeRangeOnResize: true,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#787b86', labelBackgroundColor: '#2962ff' },
        horzLine: { color: '#787b86', labelBackgroundColor: '#2962ff' },
      },
      // Déplacement : molette, glissement à la souris, glissement horizontal
      // au doigt. Le glissement VERTICAL au doigt reste à la page — sans quoi
      // la page devient impossible à parcourir dès que le pouce touche le
      // graphique.
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      // Zoom : molette, pincement à deux doigts, étirement des axes, et
      // double-clic sur un axe pour revenir à l'ajustement automatique.
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: { time: true, price: true },
        axisDoubleClickReset: { time: true, price: true },
      },
      autoSize: true,
    })

    chart.current = instance
    const candlestick = instance.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    })
    series.current = candlestick

    onReadyRef.current?.({
      priceToY: (price) => candlestick.priceToCoordinate(price),
      yToPrice: (y) => candlestick.coordinateToPrice(y) as number | null,
      timeToX: (time) => instance.timeScale().timeToCoordinate(time as never),
      xToTime: (x) => instance.timeScale().coordinateToTime(x) as number | null,
      fit: () => {
        instance.timeScale().fitContent()
        // Réactive l'échelle automatique des prix : après avoir étiré l'axe à
        // la main, « ajuster » doit vraiment tout remettre d'aplomb.
        instance.priceScale('right').applyOptions({ autoScale: true })
      },
      addLine: (options) => {
        if (disposed) return null
        const series = instance.addSeries(LineSeries, {
          ...lineOptions(options),
          // Ni étiquette sur l'axe, ni ligne de dernier prix : avec trois
          // indicateurs, l'échelle des prix se couvrirait de pastilles et le
          // prix réel — le seul qui compte — deviendrait introuvable.
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        })
        let removed = false
        return {
          setData: (points) => {
            if (removed || disposed) return
            series.setData(points.map((point) => ({ time: point.time as never, value: point.value })))
          },
          update: (point) => {
            if (removed || disposed) return
            series.update({ time: point.time as never, value: point.value })
          },
          applyOptions: (next) => {
            if (removed || disposed) return
            series.applyOptions(lineOptions(next))
          },
          remove: () => {
            // Le graphique peut avoir été détruit AVANT que React ne démonte
            // le composant qui tient cette poignée : retirer une série d'un
            // graphique disparu lève. On ne le fait donc qu'à bon escient.
            if (removed || disposed) return
            removed = true
            instance.removeSeries(series)
          },
        }
      },
    })

    return () => {
      disposed = true
      onReadyRef.current?.(null)
      instance.remove()
      chart.current = null
      series.current = null
      priceLines.current = []
      shown.current = []
    }
  }, [])

  // --- Unité de la paire ----------------------------------------------------
  useEffect(() => {
    series.current?.applyOptions({
      priceFormat: { type: 'price', precision: digits, minMove: 1 / 10 ** digits },
    })
  }, [digits])

  // --- Échelle : les niveaux restent toujours visibles ----------------------
  useEffect(() => {
    if (!series.current) return

    const prices = includePrices.filter((value) => Number.isFinite(value) && value > 0)
    series.current.applyOptions({
      autoscaleInfoProvider: (original: () => AutoscaleInfo | null) => {
        const base = original()
        if (!base?.priceRange || prices.length === 0) return base

        // On élargit la plage juste ce qu'il faut, avec une marge de 5 % pour
        // que la poignée ne colle pas au bord.
        let low = Math.min(base.priceRange.minValue, ...prices)
        let high = Math.max(base.priceRange.maxValue, ...prices)

        // Filet de sécurité : l'élargissement est plafonné à quatre fois
        // l'amplitude des bougies. Un niveau très éloigné — saisie de 5 000
        // pips, ou niveau hérité d'une autre paire le temps d'un rendu —
        // étirait l'échelle au point de réduire les bougies à un trait plat.
        // Passé ce plafond, mieux vaut une poignée au bord du cadre qu'un
        // graphique illisible.
        const span = base.priceRange.maxValue - base.priceRange.minValue
        if (span > 0) {
          const middle = (base.priceRange.maxValue + base.priceRange.minValue) / 2
          const half = (span * 4) / 2
          low = Math.max(low, middle - half)
          high = Math.min(high, middle + half)
        }

        const margin = (high - low) * 0.05
        return {
          ...base,
          priceRange: { minValue: low - margin, maxValue: high + margin },
        }
      },
    })
  }, [includePrices.join(',')])

  // --- Données --------------------------------------------------------------
  useEffect(() => {
    if (!series.current) return

    /*
     * Mise à jour de la seule dernière bougie, quand c'est tout ce qui change.
     *
     * Les cotations en direct remplacent la dernière bougie — ou en ajoutent
     * une — jusqu'à quatre fois par seconde. Recharger à chaque fois les
     * deux mille cinq cents points de la série ferait tourner le téléphone
     * pour rien. Le repli des cotations PARTAGE toutes les bougies
     * précédentes : si l'avant-dernière est le même objet qu'avant, seule la
     * fin a bougé. Un rechargement complet — autre paire, rafraîchissement
     * du courtier — produit des objets neufs, et repasse par `setData`.
     */
    const previous = shown.current
    shown.current = candles
    const growth = candles.length - previous.length
    const anchor = candles.length - 2
    if ((growth === 0 || growth === 1) && anchor >= 0 && candles[anchor] === previous[anchor]) {
      series.current.update(toBar(candles[candles.length - 1]))
      return
    }

    series.current.setData(candles.map(toBar))

    // Recadrage uniquement au changement de paire ou d'intervalle, ET
    // seulement quand des bougies sont là. Sinon le recadrage était consommé
    // par le rendu intermédiaire — celui où la clé a déjà changé mais où les
    // nouvelles bougies ne sont pas encore arrivées : on cadrait sur les
    // anciennes, puis plus jamais sur les bonnes.
    if (candles.length > 0 && fitted.current !== fitKey) {
      fitted.current = fitKey
      chart.current?.timeScale().fitContent()
    }
  }, [candles, fitKey])

  // --- Lignes de prix (entrée, SL, TP — alimentées à l'étape 7) -------------
  useEffect(() => {
    if (!series.current) return

    for (const line of priceLines.current) series.current.removePriceLine(line)
    priceLines.current = markers.map((marker) =>
      series.current!.createPriceLine({
        price: marker.price,
        color: marker.color,
        lineWidth: 1,
        lineStyle: marker.dashed ? LineStyle.Dashed : LineStyle.Solid,
        axisLabelVisible: true,
        title: marker.title,
      }),
    )
  }, [markers])

  return (
    // `overflow-hidden` : un niveau hors de la plage visible sortait du cadre
    // et venait recouvrir le sélecteur d'intervalles au-dessus.
    <div className="relative overflow-hidden" style={{ height }}>
      <div
        ref={container}
        style={{ height }}
        // `touch-action: pan-y` laisse le défilement vertical à la page, et
        // le pincement au graphique.
        className="w-full touch-pan-y"
      />
      {children}
    </div>
  )
}
